import React, { useCallback } from 'react';
import { connect } from 'react-redux';

import icoEmail from 'assets/images/ico-email.svg';
import icoPhone from 'assets/images/ico-phone.svg';
import Button from 'components/form/Button.component';
import Tooltip from 'components/generic/Tooltip.component';
import StripeAccountVerifierModal from 'components/modal/StripeAccountVerifierModal.component';
import S from 'constants/string.const';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { withSalonRestriction } from 'providers/SalonBasicInfo.provider';
import { selectHideDetails } from 'selectors/user';
import fromStringToXs from 'utils/converters/fromStringToXs';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';

import SmsButtonNew from 'scheduler/components/common/SmsButtonNew';

import CN from './ClientContactInfo.module.scss';

const ButtonText = () => {
  const { t } = useTranslation();
  return (
    <span className={CN.button}>
      {t('composite.clientProfile.contactInfo.textClientButton')} {S.speechBalloon}
      {S.space}
      {S.mobilePhone}
    </span>
  );
};

const ClientContactInfo = ({
  client,
  hideDetails,
  goToEditDetails,
  isRestrictedToCriticalServices,
  isRestrictionCheckEnabled,
  enableRestrictionCheck,
  disableRestrictionCheck,
}) => {
  const { id: clientId, user_name: clientName, email, phone, alternative_phone: alternativePhone } = client ?? {};

  const { val: isSmsRendered, yay: showSms, set: setSmsRendered } = useTip(false);
  const { t } = useTranslation();

  const handleEditContact = useCallback(() => goToEditDetails('contact'), [goToEditDetails]);

  const handleTextClient = useCallback(() => {
    if (isRestrictedToCriticalServices) return void enableRestrictionCheck();
    showSms();
  }, [enableRestrictionCheck, isRestrictedToCriticalServices, showSms]);

  return (
    <div data-bem="ClientContactInfo">
      <ul className="client-contact-info">
        <li>
          <img src={icoPhone} alt="phone" />
          {hideDetails ? <span>{phone ? fromStringToXs(phone) : '-'}</span> : <span>{phone || '-'}</span>}
        </li>
        {alternativePhone && (
          <li>
            <img src={icoPhone} alt="phone" />
            {hideDetails ? (
              <span>{alternativePhone ? fromStringToXs(alternativePhone) : '-'}</span>
            ) : (
              <span>{alternativePhone || '-'}</span>
            )}
          </li>
        )}
        <li>
          <img src={icoEmail} alt="email" />
          <span className={hideDetails ? 'blur-text' : ''}>{email || '-'}</span>
        </li>
      </ul>

      <div className={CN.flex}>
        <div className={CN.leftButton}>
          {phone ? (
            <Button variant="senary" width="auto" onClick={handleTextClient}>
              <ButtonText />
            </Button>
          ) : (
            <Tooltip variant="top" width="xs" content={t('composite.clientProfile.contactInfo.tooltip')}>
              <Button variant="senary" width="auto" onClick={alwaysNoop} disabled={!phone}>
                <ButtonText />
              </Button>
            </Tooltip>
          )}
        </div>
        <div className={CN.rightButton}>
          <Button variant="senary" width="auto" onClick={handleEditContact}>
            <span className={CN.button}>{t('composite.clientProfile.contactInfo.editContactButton')}</span>
          </Button>
        </div>
      </div>

      {isSmsRendered && (
        <SmsButtonNew
          clientId={[clientId]}
          bucketType="one_to_one"
          clientName={clientName}
          customTitle={true}
          isOpen={isSmsRendered}
          smsComposeModalOpen={isSmsRendered}
          showParentSmsPopup={setSmsRendered}
          hideElement={true}
        />
      )}

      <StripeAccountVerifierModal
        tag="BookingItem__restriction-modal"
        enabled={isRestrictionCheckEnabled}
        onCancel={disableRestrictionCheck}
        onClose={disableRestrictionCheck}
      />
    </div>
  );
};

const ClientContactInfoHoc = connect((state) => ({
  hideDetails: selectHideDetails(state),
}))(withSalonRestriction(ClientContactInfo));

export default ClientContactInfoHoc;
